import { isAxiosError } from "axios";
import { type MutableRefObject, useCallback, useEffect, useRef } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import type { AiPrintoutShift } from "../../../downloads/aiPrintout/contract";
import { backendService } from "../../../services/backend.service";
import { errorText } from "../../../utils/errors";
import { restoreText } from "../../Careers/careersFormat";
import type { ChatEntry, CustomPrintoutDraft, DraftAction } from "./customPrintoutDraftState";
import { clearStoredDraft, readStoredDraft, type StoredChatEntry, type StoredCustomPrintoutDraft, writeStoredDraft } from "./customPrintoutDraftStorage";
import type { CustomPrintoutDetail } from "./customPrintoutTypes";

/** Below this an HTTP status is the server answering "no" for good: the design is gone or isn't this person's. */
const SERVER_ERROR_STATUS = 500;

/** Whose draft this is: the saved printout's id, or — for a new one — what it starts from. Changing it starts over. */
export const ownerKeyOf = (printoutId: string | null, startFromId: string | null): string | null => {
	if (printoutId !== null) return printoutId;
	return startFromId === null ? null : `new-from:${startFromId}`;
};

const storedEntriesOf = (entries: ChatEntry[]): StoredChatEntry[] =>
	entries.flatMap((entry): StoredChatEntry[] => {
		if (entry.kind === "turn") return [{ kind: "turn", turn: entry.turn }];
		if (entry.kind === "error") return [];
		return [{ ...entry, answer: entry.answer === "open" ? "dismissed" : entry.answer }];
	});

type Params = {
	printoutId: string | null;
	startFromId: string | null;
	ownerKey: string | null;
	/** The saved printout's version on screen; `null` for a new printout, or while it loads. */
	saved: CustomPrintoutDetail | null;
	draft: CustomPrintoutDraft | null;
	entries: ChatEntry[];
	/** The viewed day's shifts, or `null` while it loads: a restored design is checked against them. */
	shifts: AiPrintoutShift[] | null;
	/** The draft's owner and what the preview shows now, read when the stored design arrives. */
	ownerRef: MutableRefObject<string | null>;
	shownSourceRef: MutableRefObject<string | null>;
	/** Nothing has been designed since this printout was opened, so a stored design may take its place. */
	isPristine: boolean;
	dispatch: (action: DraftAction) => void;
};

/**
 * Keeps the newest unsaved design in this browser (`customPrintoutDraftStorage`) and brings it back after a reload:
 * when its printout is shown again, the design is fetched from the server — which answers only the person who asked
 * for it — and shown with the requests that led to it. A design made on a version someone has since replaced is
 * dropped. Returns `forget`, for Save and Discard.
 */
export const useDraftPersistence = ({
	printoutId,
	startFromId,
	ownerKey,
	saved,
	draft,
	entries,
	shifts,
	ownerRef,
	shownSourceRef,
	isPristine,
	dispatch,
}: Params) => {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment.department_id;
	const baseVersionId = saved === null ? null : saved.versionId;

	useEffect(() => {
		if (draft === null) return;
		writeStoredDraft(departmentId, {
			printoutId,
			startFromId,
			baseVersionId,
			generationId: draft.generationId,
			effort: draft.effort,
			entries: storedEntriesOf(entries),
		});
	}, [departmentId, printoutId, startFromId, baseVersionId, draft, entries]);

	const restore = useRestore({ departmentId, ownerRef, shownSourceRef, dispatch });
	/** The owner whose stored design has been looked for; `undefined` before the first look. */
	const checkedOwnerRef = useRef<string | null | undefined>(undefined);
	useEffect(() => {
		if (checkedOwnerRef.current === ownerKey || !isPristine || shifts === null) return;
		const stored = readStoredDraft(departmentId);
		const isForThisOwner = stored !== null && ownerKeyOf(stored.printoutId, stored.startFromId) === ownerKey;
		// A saved printout's stored design is checked against the version it was made on, so that has to load first.
		if (isForThisOwner && printoutId !== null && saved === null) return;
		checkedOwnerRef.current = ownerKey;
		if (!isForThisOwner) return;
		if (stored.baseVersionId !== baseVersionId) {
			clearStoredDraft(departmentId);
			return;
		}
		restore(stored, ownerKey, shifts);
	}, [departmentId, ownerKey, printoutId, saved, baseVersionId, isPristine, shifts, restore]);

	return useCallback(() => clearStoredDraft(departmentId), [departmentId]);
};

type RestoreParams = {
	departmentId: string;
	ownerRef: MutableRefObject<string | null>;
	shownSourceRef: MutableRefObject<string | null>;
	dispatch: (action: DraftAction) => void;
};

/** Fetches a stored design and shows it, unless the user has moved on (or started designing) by the time it arrives. */
const useRestore = ({ departmentId, ownerRef, shownSourceRef, dispatch }: RestoreParams) => {
	const { track, trackError } = useAnalytics();
	return useCallback(
		(stored: StoredCustomPrintoutDraft, ownerKey: string | null, shifts: AiPrintoutShift[]): void => {
			backendService
				.getCustomPrintoutGeneration(departmentId, stored.generationId)
				.then(({ data }) => {
					// Only a finished design is ever stored, so anything else means it is gone (or was never this person's).
					if (data.status !== "succeeded" || data.source === null || data.summary === null || data.coverage === null) {
						clearStoredDraft(departmentId);
						return;
					}
					if (ownerRef.current !== ownerKey) return;
					// Annie writes the summary, so it can be a bare date that `axiosInstance` turned into a DateTime.
					const restoredDraft = {
						generationId: data.generationId,
						source: data.source,
						summary: restoreText(data.summary),
						effort: stored.effort,
						coverage: data.coverage,
					};
					dispatch({ type: "restored", draft: restoredDraft, entries: stored.entries, sameSourceAsShown: data.source === shownSourceRef.current, shifts });
					track("custom_printout_draft_restored", { department_id: departmentId, printout_id: stored.printoutId });
				})
				.catch((error: unknown) => {
					// Gone or not this person's: forget it. A network blip or a 5xx: keep it for the next reload.
					if (isAxiosError(error) && error.response !== undefined && error.response.status < SERVER_ERROR_STATUS) clearStoredDraft(departmentId);
					trackError("custom_printout_draft_restore", { department_id: departmentId, error_message: errorText(error) });
				});
		},
		[departmentId, ownerRef, shownSourceRef, dispatch, track, trackError],
	);
};
